import React, { useCallback, useEffect, useMemo, useState } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table, Tabs, Tab } from 'react-bootstrap';
import { FiUsers, FiCheckCircle, FiCalendar, FiXCircle } from 'react-icons/fi';
import PropTypes from 'prop-types';
import { fileAPI } from '../utils/api';
import './NfsFrtTechnicianDashboardPage.css';

const pct = (v) => `${Number(v || 0).toFixed(1)}%`;

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateOnly = (value) => {
  if (!value) return '-';
  return String(value).slice(0, 10);
};

const formatTimeAgo = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const diffMinutes = Math.max(0, Math.round((Date.now() - date.getTime()) / (1000 * 60)));
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
  const diffHours = diffMinutes / 60;
  return `${diffHours.toFixed(1)}h ago`;
};

const STAGE_STATUS_META = {
  NFS_evening_approval: { icon: '🔒', label: 'Stage 4' },
  NFS_evening_login: { icon: '🌆', label: 'Stage 3' },
  NFS_morning_approval: { icon: '✅', label: 'Stage 2' },
  NFS_morning_login: { icon: '🌅', label: 'Stage 1' }
};

const buildAttendanceFilterOptions = (attendanceRows) => {
  const normalizedRows = Array.isArray(attendanceRows) ? attendanceRows : [];
  const regions = normalizedRows.map((row) => String(row?.region || '').trim()).filter(Boolean);
  const partners = normalizedRows.map((row) => String(row?.partnerName || '').trim()).filter(Boolean);
  return {
    regionOptions: Array.from(new Set(regions)).sort((a, b) => a.localeCompare(b)),
    partnerOptions: Array.from(new Set(partners)).sort((a, b) => a.localeCompare(b))
  };
};

const filterAttendanceRows = ({ attendanceRows, regionFilter, partnerFilter }) => {
  const normalizedRows = Array.isArray(attendanceRows) ? attendanceRows : [];
  return normalizedRows.filter((row) => {
    const regionMatch = regionFilter === 'all' || String(row?.region || '').trim() === regionFilter;
    const partnerMatch = partnerFilter === 'all' || String(row?.partnerName || '').trim() === partnerFilter;
    return regionMatch && partnerMatch;
  });
};

const getAttendanceSummaryPercent = (attendanceSummary, name) => {
  const list = Array.isArray(attendanceSummary) ? attendanceSummary : [];
  return list.find((s) => s?.name === name)?.percentage || 0;
};

const computeFilteredStats = ({ data, regionFilter, partnerFilter, filteredAttendanceRows }) => {
  if (!data) {
    return { totalCount: 0, present: 0, onLeave: 0, absent: 0, presentPct: 0, onLeavePct: 0, absentPct: 0 };
  }

  if (regionFilter === 'all' && partnerFilter === 'all') {
    return {
      ...data.totals,
      presentPct: getAttendanceSummaryPercent(data.attendanceSummary, 'Present'),
      onLeavePct: getAttendanceSummaryPercent(data.attendanceSummary, 'On Leave'),
      absentPct: getAttendanceSummaryPercent(data.attendanceSummary, 'Absent')
    };
  }

  const totalCount = filteredAttendanceRows.length;
  const present = filteredAttendanceRows.filter((r) => r.attendanceStatus === 'Present').length;
  const onLeave = filteredAttendanceRows.filter((r) => r.attendanceStatus === 'On Leave').length;
  const absent = totalCount - present - onLeave;

  return {
    totalCount, present, onLeave, absent,
    presentPct: totalCount > 0 ? (present / totalCount) * 100 : 0,
    onLeavePct: totalCount > 0 ? (onLeave / totalCount) * 100 : 0,
    absentPct: totalCount > 0 ? (absent / totalCount) * 100 : 0
  };
};

const NfsFrtTechnicianDashboardView = ({
  error,
  syncSuccessMessage,
  canSeePage,
  selectedDate,
  onSetSelectedDate,
  roleFilter,
  onSetRoleFilter,
  regionFilter,
  onSetRegionFilter,
  partnerFilter,
  onSetPartnerFilter,
  regionOptions,
  partnerOptions,
  syncing,
  syncingStage,
  onSyncStage,
  onSyncAll,
  filteredStats,
  filteredAttendanceRows,
  activeStageTab,
  onSetActiveStageTab,
  stageTabMeta,
  data
}) => (
  <Container fluid className="om-dashboard-page py-4">
    {error && <Alert variant="danger">{error}</Alert>}
    {syncSuccessMessage && <Alert variant="success">{syncSuccessMessage}</Alert>}

    {!canSeePage ? (
      <Alert variant="warning">NFS FRT & Technician dashboard is available only for O&M users and EXCO.</Alert>
    ) : (
      <>
        <Row className="g-2 mb-2 align-items-end flex-wrap">
          <Col xs="auto">
            <Form.Control style={{ width: 190 }} type="date" value={selectedDate} onChange={(e) => onSetSelectedDate(e.target.value)} />
          </Col>
          <Col xs="auto">
            <Form.Select style={{ width: 140 }} value={roleFilter} onChange={(e) => onSetRoleFilter(e.target.value)}>
              <option value="FRT">FRT</option>
              <option value="TECHNICIAN">Technician</option>
            </Form.Select>
          </Col>
          <Col xs="auto">
            <Form.Select style={{ width: 150 }} value={regionFilter} onChange={(e) => onSetRegionFilter(e.target.value)}>
              <option value="all">All Regions</option>
              {regionOptions.map((region) => (
                <option key={region} value={region}>{region}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs="auto">
            <Form.Select style={{ width: 150 }} value={partnerFilter} onChange={(e) => onSetPartnerFilter(e.target.value)}>
              <option value="all">All Partners</option>
              {partnerOptions.map((partner) => (
                <option key={partner} value={partner}>{partner}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>

        <Row className="g-2 mb-3 align-items-end flex-wrap">
          <Col xs="auto">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => onSyncStage('morning-login', 'Stage 1: Morning Login')}
              disabled={syncing || !!syncingStage}
            >
              {syncingStage === 'morning-login' ? 'Syncing...' : 'Stage 1 Sync'}
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => onSyncStage('morning-approval', 'Stage 2: Morning Approval')}
              disabled={syncing || !!syncingStage}
            >
              {syncingStage === 'morning-approval' ? 'Syncing...' : 'Stage 2 Sync'}
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => onSyncStage('evening-login', 'Stage 3: Evening Login')}
              disabled={syncing || !!syncingStage}
            >
              {syncingStage === 'evening-login' ? 'Syncing...' : 'Stage 3 Sync'}
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => onSyncStage('evening-approval', 'Stage 4: Evening Approval')}
              disabled={syncing || !!syncingStage}
            >
              {syncingStage === 'evening-approval' ? 'Syncing...' : 'Stage 4 Sync'}
            </Button>
          </Col>
          <Col xs="auto" className="ms-auto">
            <Button onClick={onSyncAll} disabled={syncing || !!syncingStage}>
              {(syncing || syncingStage === 'all') ? 'Syncing...' : 'Sync NFS Data'}
            </Button>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col md={3}>
            <Card className="stat-card stat-primary h-100">
              <Card.Body className="d-flex align-items-center gap-2">
                <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiUsers /></div>
                <div>
                  <p className="stat-title mb-1">Total Staff</p>
                  <h4 className="mb-0 fw-bold">{filteredStats?.totalCount ?? 0}</h4>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card stat-success h-100">
              <Card.Body className="d-flex align-items-center gap-2">
                <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiCheckCircle /></div>
                <div>
                  <p className="stat-title mb-1">Present</p>
                  <h4 className="mb-0 fw-bold">{filteredStats?.present ?? 0}</h4>
                  <small className="text-muted">{pct(filteredStats?.presentPct)}</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card stat-warning h-100">
              <Card.Body className="d-flex align-items-center gap-2">
                <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiCalendar /></div>
                <div>
                  <p className="stat-title mb-1">On Leave</p>
                  <h4 className="mb-0 fw-bold">{filteredStats?.onLeave ?? 0}</h4>
                  <small className="text-muted">{pct(filteredStats?.onLeavePct)}</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card stat-danger h-100">
              <Card.Body className="d-flex align-items-center gap-2">
                <div className="stat-icon" style={{ width: 36, height: 36, fontSize: 16 }}><FiXCircle /></div>
                <div>
                  <p className="stat-title mb-1">Absent</p>
                  <h4 className="mb-0 fw-bold">{filteredStats?.absent ?? 0}</h4>
                  <small className="text-muted">{pct(filteredStats?.absentPct)}</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="shadow-sm mb-3">
          <Card.Header className="bg-white border-bottom">
            <h6 className="mb-0">{roleFilter === 'TECHNICIAN' ? 'Detailed Technician Attendance' : 'Detailed FRT Attendance'}</h6>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table striped bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>{roleFilter === 'TECHNICIAN' ? 'Technician ID' : 'FRT ID'}</th>
                    <th>Partner Name</th>
                    <th>Region</th>
                    <th>Cluster</th>
                    <th>Contact Person</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendanceRows.slice(0, 500).map((row, index) => (
                    <tr key={row.id || index}>
                      <td>{row.displayId || row.id || '-'}</td>
                      <td>{row.partnerName || '-'}</td>
                      <td>{row.region || '-'}</td>
                      <td>{row.cluster || '-'}</td>
                      <td>{row.contactPerson || '-'}</td>
                      <td>
                        <Badge bg={row.attendanceStatus === 'Present' ? 'success' : row.attendanceStatus === 'On Leave' ? 'warning' : 'secondary'} text={row.attendanceStatus === 'On Leave' ? 'dark' : undefined}>
                          {row.attendanceStatus || '-'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                  {!filteredAttendanceRows.length && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted">No attendance rows for selected filters.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-3">
          <Card.Header className="bg-white border-bottom">
            <h6 className="mb-0">4-Stage Detailed Data</h6>
          </Card.Header>
          <Card.Body>
            <Row className="g-2 mb-3">
              <Col md={6} lg={3}><Card className="om-stage-kpi-card h-100"><Card.Body><p className="om-stage-kpi-title mb-1">Morning Login</p><h5 className="om-stage-kpi-value mb-0">{data?.stageFlow?.morningLoginCount ?? 0}</h5></Card.Body></Card></Col>
              <Col md={6} lg={3}><Card className="om-stage-kpi-card h-100"><Card.Body><p className="om-stage-kpi-title mb-1">Morning Approval</p><h5 className="om-stage-kpi-value mb-0">{data?.stageFlow?.morningApprovalCount ?? 0}</h5></Card.Body></Card></Col>
              <Col md={6} lg={3}><Card className="om-stage-kpi-card h-100"><Card.Body><p className="om-stage-kpi-title mb-1">Evening Login</p><h5 className="om-stage-kpi-value mb-0">{data?.stageFlow?.eveningLoginCount ?? 0}</h5></Card.Body></Card></Col>
              <Col md={6} lg={3}><Card className="om-stage-kpi-card h-100"><Card.Body><p className="om-stage-kpi-title mb-1">Evening Approval</p><h5 className="om-stage-kpi-value mb-0">{data?.stageFlow?.eveningApprovalCount ?? 0}</h5></Card.Body></Card></Col>
            </Row>
            <Tabs activeKey={activeStageTab} onSelect={(k) => onSetActiveStageTab(k || 'morningLogin')} className="om-stage-tabs-horizontal">
              {Object.entries(stageTabMeta).map(([key, meta]) => (
                <Tab key={key} eventKey={key} title={meta.title}>
                  <div className="table-responsive mt-3">
                    <Table striped bordered hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          {meta.columns.map((column) => <th key={column[0]}>{column[1]}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {meta.rows.length ? meta.rows.slice(0, 500).map((row, index) => (
                          <tr key={row.fieldUserLoginId || index}>
                            {meta.columns.map((column) => {
                              const value = row?.[column[0]];
                              const rendered = column[0] === 'uploadTime' ? formatDateTime(value) : (value ?? '-');
                              return <td key={`${column[0]}-${index}`}>{rendered}</td>;
                            })}
                          </tr>
                        )) : (
                          <tr><td colSpan={meta.columns.length} className="text-center text-muted">No records found.</td></tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Tab>
              ))}
            </Tabs>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-3">
          <Card.Header className="bg-white border-bottom"><h6 className="mb-0">🔄 Auto-Sync Status</h6></Card.Header>
          <Card.Body>
            <p className="mb-2">{`✅ Last synced: ${formatTimeAgo(data?.dbStatus?.lastSyncedAt)}`}</p>
            <p className="mb-3">{`📅 Auto-sync runs every ${Number(data?.dbStatus?.autoSyncHours || 4)} hours`}</p>
            <p className="mb-2"><strong>Stage Status:</strong></p>
            {Object.keys(STAGE_STATUS_META).map((tableName) => {
              const row = (data?.dbStatus?.lastSync || []).find((item) => item.tableName === tableName);
              const meta = STAGE_STATUS_META[tableName];
              if (!row) return null;
              return (
                <p key={`stage-${tableName}`} className="mb-1">
                  {`${meta.icon} ${meta.label}: ${formatTimeAgo(row.lastSyncAt)} (${Number(row.recordsSynced || 0).toLocaleString('en-IN')} records)`}
                </p>
              );
            })}
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-3">
          <Card.Header className="bg-white border-bottom"><h6 className="mb-0">📊 Database Status</h6></Card.Header>
          <Card.Body>
            <p className="mb-2"><strong>Record Counts:</strong></p>
            <p className="mb-1">{`• Morning Login: ${Number(data?.dbStatus?.counts?.morningLoginCount || 0).toLocaleString('en-IN')}`}</p>
            <p className="mb-1">{`• Morning Approval: ${Number(data?.dbStatus?.counts?.morningApprovalCount || 0).toLocaleString('en-IN')}`}</p>
            <p className="mb-1">{`• Evening Login: ${Number(data?.dbStatus?.counts?.eveningLoginCount || 0).toLocaleString('en-IN')}`}</p>
            <p className="mb-3">{`• Evening Approval: ${Number(data?.dbStatus?.counts?.eveningApprovalCount || 0).toLocaleString('en-IN')}`}</p>
            <p className="mb-3">{`Date Range: ${formatDateOnly(data?.dbStatus?.dateRange?.minDate)} to ${formatDateOnly(data?.dbStatus?.dateRange?.maxDate)}`}</p>
            <p className="mb-2"><strong>Master Data:</strong></p>
            <p className="mb-1">{`• Total FRTs: ${Number(data?.dbStatus?.masterData?.totalFrts || 0).toLocaleString('en-IN')}`}</p>
            <p className="mb-0">{`• Total Technicians: ${Number(data?.dbStatus?.masterData?.totalTechnicians || 0).toLocaleString('en-IN')}`}</p>
          </Card.Body>
        </Card>

        <div className="border rounded p-3 mt-3 bg-white">
          <p className="mb-1"><strong>Technician 4-Stage Attendance Monitoring Features:</strong></p>
          <p className="mb-1">New Columns: Region, Cluster, Partner_Name for better organization</p>
          <p className="mb-1">Updated ID Tracking: FRT_ID for FRTs, Patroller_Name for Technicians</p>
          <p className="mb-0">Stage 1-4: Complete attendance workflow tracking</p>
        </div>
      </>
    )}
  </Container>
);

const NfsFilteredStatsPropType = PropTypes.shape({
  totalCount: PropTypes.number,
  present: PropTypes.number,
  onLeave: PropTypes.number,
  absent: PropTypes.number,
  presentPct: PropTypes.number,
  onLeavePct: PropTypes.number,
  absentPct: PropTypes.number
});

NfsFrtTechnicianDashboardView.propTypes = {
  error: PropTypes.string,
  syncSuccessMessage: PropTypes.string,
  canSeePage: PropTypes.bool.isRequired,
  selectedDate: PropTypes.string.isRequired,
  onSetSelectedDate: PropTypes.func.isRequired,
  roleFilter: PropTypes.string.isRequired,
  onSetRoleFilter: PropTypes.func.isRequired,
  regionFilter: PropTypes.string.isRequired,
  onSetRegionFilter: PropTypes.func.isRequired,
  partnerFilter: PropTypes.string.isRequired,
  onSetPartnerFilter: PropTypes.func.isRequired,
  regionOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  partnerOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  syncing: PropTypes.bool.isRequired,
  syncingStage: PropTypes.string,
  onSyncStage: PropTypes.func.isRequired,
  onSyncAll: PropTypes.func.isRequired,
  filteredStats: NfsFilteredStatsPropType.isRequired,
  filteredAttendanceRows: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeStageTab: PropTypes.string.isRequired,
  onSetActiveStageTab: PropTypes.func.isRequired,
  stageTabMeta: PropTypes.object.isRequired,
  data: PropTypes.object
};

const NfsFrtTechnicianDashboardPage = ({ departmentId = null }) => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingStage, setSyncingStage] = useState('');
  const [syncSuccessMessage, setSyncSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [canSeePage, setCanSeePage] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [roleFilter, setRoleFilter] = useState('FRT');
  const [regionFilter, setRegionFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [data, setData] = useState(null);
  const [activeStageTab, setActiveStageTab] = useState('morningLogin');

  const attendanceRows = data?.attendanceDetails || [];
  const { regionOptions, partnerOptions } = useMemo(
    () => buildAttendanceFilterOptions(attendanceRows),
    [attendanceRows]
  );
  const filteredAttendanceRows = useMemo(
    () => filterAttendanceRows({ attendanceRows, regionFilter, partnerFilter }),
    [attendanceRows, regionFilter, partnerFilter]
  );

  const filteredStats = useMemo(() => {
    return computeFilteredStats({ data, regionFilter, partnerFilter, filteredAttendanceRows });
  }, [data, regionFilter, partnerFilter, filteredAttendanceRows]);

  const fetchDashboard = useCallback(async () => {
    if (!departmentId) {
      setLoading(false);
      setError('Department is required to load NFS FRT & Technician dashboard.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await fileAPI.getNfsFrtTechnicianDashboard({
        departmentId,
        date: selectedDate,
        roleFilter
      });
      setData(response?.data || null);
      setCanSeePage(true);
    } catch (err) {
      const message = typeof err === 'string' ? err : (err?.message || err?.error || 'Failed to load NFS FRT & Technician dashboard.');
      const status = err?.status || err?.statusCode || 0;

      // More robust check for unauthorized access
      if (String(message).includes('403') || status === 403) setCanSeePage(false);
      else { setCanSeePage(true); setError(message); }
    } finally {
      setLoading(false);
    }
  }, [departmentId, selectedDate, roleFilter]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    setPartnerFilter('all');
  }, [roleFilter]);

  const handleSyncAll = async () => {
    if (!departmentId) return;
    try {
      setSyncing(true);
      setSyncingStage('all');
      setError('');
      setSyncSuccessMessage('');
      const response = await fileAPI.syncNfsFrtTechnicianDashboard({ departmentId, fullHistory: false, days: 30 });
      await fetchDashboard();
      if (response?.message) {
        setSyncSuccessMessage(response.message);
      } else {
        setSyncSuccessMessage('NFS sync started in the background.');
      }
    } catch (err) {
      const message = typeof err === 'string' ? err : (err?.message || err?.error || 'Failed to sync NFS dashboard.');
      setError(message);
    } finally {
      setSyncing(false);
      setSyncingStage('');
    }
  };

  const handleSyncStage = async (stageKey, stageLabel) => {
    if (!departmentId) return;
    try {
      setSyncingStage(stageKey);
      setError('');
      setSyncSuccessMessage('');
      const response = await fileAPI.syncNfsFrtTechnicianDashboard({ departmentId, fullHistory: false, days: 30, stage: stageKey });
      await fetchDashboard();
      if (response?.message) {
        setSyncSuccessMessage(response.message);
      } else {
        setSyncSuccessMessage(`${stageLabel} sync started in the background.`);
      }
    } catch (err) {
      const message = typeof err === 'string' ? err : (err?.message || err?.error || `Failed to sync ${stageLabel}.`);
      setError(message);
    } finally {
      setSyncingStage('');
    }
  };

  const stageTabMeta = {
    morningLogin: {
      title: 'Morning Login',
      rows: data?.stageRows?.morningLogin || [],
      columns: [
        ['fieldUserLoginId', 'Login ID'],
        ['fieldUser', 'Name'],
        ['partnerName', 'Partner Name'],
        ['region', 'Region'],
        ['cluster', 'Cluster'],
        ['workingStatus', 'Attendance Status'],
        ['startReading', 'Start Reading'],
        ['uploadTime', 'Upload Time']
      ]
    },
    morningApproval: {
      title: 'Morning Approval',
      rows: data?.stageRows?.morningApproval || [],
      columns: [
        ['fieldUserLoginId', 'Login ID'],
        ['fieldUser', 'Name'],
        ['partnerName', 'Partner Name'],
        ['region', 'Region'],
        ['cluster', 'Cluster'],
        ['approvedByEngineer', 'Approved'],
        ['engineerRemarks', 'Engineer Remarks'],
        ['uploadTime', 'Upload Time']
      ]
    },
    eveningLogin: {
      title: 'Evening Login',
      rows: data?.stageRows?.eveningLogin || [],
      columns: [
        ['fieldUserLoginId', 'Login ID'],
        ['fieldUser', 'Name'],
        ['partnerName', 'Partner Name'],
        ['region', 'Region'],
        ['cluster', 'Cluster'],
        ['endReadingAsPerVehicleMeter', 'End Reading'],
        ['uploadTime', 'Upload Time']
      ]
    },
    eveningApproval: {
      title: 'Evening Approval',
      rows: data?.stageRows?.eveningApproval || [],
      columns: [
        ['fieldUserLoginId', 'Login ID'],
        ['fieldUser', 'Name'],
        ['partnerName', 'Partner Name'],
        ['region', 'Region'],
        ['cluster', 'Cluster'],
        ['endEntryApprovedByEngineer', 'Approved'],
        ['finalEngineerRemarks', 'Final Engineer Remarks'],
        ['uploadTime', 'Upload Time']
      ]
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 mb-0">Loading NFS FRT & Technician dashboard...</p>
      </div>
    );
  }

  return (
    <NfsFrtTechnicianDashboardView
      error={error}
      syncSuccessMessage={syncSuccessMessage}
      canSeePage={canSeePage}
      selectedDate={selectedDate}
      onSetSelectedDate={setSelectedDate}
      roleFilter={roleFilter}
      onSetRoleFilter={setRoleFilter}
      regionFilter={regionFilter}
      onSetRegionFilter={setRegionFilter}
      partnerFilter={partnerFilter}
      onSetPartnerFilter={setPartnerFilter}
      regionOptions={regionOptions}
      partnerOptions={partnerOptions}
      syncing={syncing}
      syncingStage={syncingStage}
      onSyncStage={handleSyncStage}
      onSyncAll={handleSyncAll}
      filteredStats={filteredStats}
      filteredAttendanceRows={filteredAttendanceRows}
      activeStageTab={activeStageTab}
      onSetActiveStageTab={setActiveStageTab}
      stageTabMeta={stageTabMeta}
      data={data}
    />
  );
};

NfsFrtTechnicianDashboardPage.propTypes = {
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default NfsFrtTechnicianDashboardPage;
